// Party + document detail endpoints. Read-only at this stage; the
// addPhone POST is the long-standing manual phone-entry path.

import { authHeaders } from '../../lib/authHeaders';

export interface PartyDetailParty {
  id: string;
  display_name: string;
  display_name_natural: string | null;
  normalized_name: string;
  display_suffix: string | null;
  entity_type: string;
  family_cluster_id: string | null;
  family_cluster_confidence: number | null;
  first_seen_date: string | null;
  last_activity_date: string | null;
  origin_pool_order_id: string | null;
  notes: string | null;
  last_pushed_to_close_at: string | null;
  skipped_at: string | null;
  skip_trace_city: string | null;
  skip_trace_state: string | null;
  skip_trace_location_source: string | null;
  skip_trace_location_set_at: string | null;
  created_at: string;
  updated_at: string;
  has_phone: boolean;
  has_address: boolean;
}

export interface PartyAlias {
  id: string; surface_form: string; normalized_form: string;
  suffix: string | null; created_at: string;
}

export interface PartyAddress {
  id: string; street: string | null; city: string | null; state: string | null;
  postal: string | null; country: string | null; source: string;
  source_doc_number: string | null; lease_effective_date: string | null;
  confidence: number | null; is_current: boolean; acquired_at: string;
}

export interface PartyPhone {
  id: string; phone_e164: string; source: string; source_doc_number: string | null;
  confidence: number | null; is_current: boolean; acquired_at: string;
}

export interface PartyEmail {
  id: string; email: string; source: string; source_doc_number: string | null;
  confidence: number | null; is_current: boolean; acquired_at: string;
}

export interface PartyDocument {
  doc_number: string; role: 'grantor' | 'grantee'; ordinal: number;
  doc_type: string | null; recording_date: string | null;
  effective_date: string | null; primary_term_months: number | null;
  royalty_pct: string | null; gross_acreage: string | null;
  lessee: string | null; enrichment_status: string | null;
  trs_label: string | null;
  /** The grantee party name(s) on this document ("sold to X" for a
   *  mineral/royalty deed the party granted). '/'-joined when several. */
  grantee_names: string | null;
}

export interface PartyAoi { id: string; name: string; basin: string | null; }

export interface PartyPipelinePush {
  build_batch_id: string; source_kind: 'aoi' | 'pool_order';
  pushed_by: string; pushed_at: string;
  source_aoi_id: string | null; source_pool_order_id: string | null;
  source_label: string | null;
}

export interface PartyPoolOrder {
  id: string; docket_number: string | null; operator: string | null;
  filed_date: string | null; match_confidence: number | null;
}

export interface PartyLeasedOwnerRow {
  id: string; pool_order_id: string; docket_number: string | null;
  pool_operator: string | null; lease_date: string | null;
  lessee_name: string | null; matched_doc_number: string | null;
  tract_description: string | null;
}

export interface PartyLeadListSummary {
  pending: number;
  researched: number;
  pushed: number;
  dropped: number;
}

export interface PartyDetailResponse {
  party: PartyDetailParty;
  aliases: PartyAlias[];
  addresses: PartyAddress[];
  phones: PartyPhone[];
  emails: PartyEmail[];
  documents: PartyDocument[];
  aois: PartyAoi[];
  pipeline_pushes: PartyPipelinePush[];
  pool_orders: PartyPoolOrder[];
  leased_owner_rows: PartyLeasedOwnerRow[];
  lead_list_summary: PartyLeadListSummary;
}

export interface DocumentDetailParty {
  party_id: string; ordinal: number; display_name: string;
  display_name_natural: string | null; entity_type: string;
}

export interface DocumentTrs {
  township_num: number; township_dir: 'N' | 'S';
  range_num: number; range_dir: 'E' | 'W'; section: number; trs_key: string;
}

export interface DocumentDetail {
  doc_number: string; doc_type: string | null; recording_date: string | null;
  raw_grantor_text: string | null; raw_grantee_text: string | null;
  raw_legal_description: string | null;
  effective_date: string | null; primary_term_months: number | null;
  royalty_pct: number | null; gross_acreage: number | null; bonus: number | null;
  lessee: string | null; enrichment_status: string | null;
  review_conflict: boolean; review_conflict_fields: unknown[];
  review_no_recorder_match: boolean;
  created_at: string; updated_at: string;
}

export interface DocumentBatchEvent {
  id: string; csv_batch_id: string; event_type: string;
  changed_fields: unknown; created_at: string;
}

export interface DocumentReferencedPoolOrder {
  id: string; pool_order_id: string; docket_number: string | null;
  lessor_name: string; lessee_name: string | null; lease_date: string | null;
}

export interface DocumentDetailResponse {
  document: DocumentDetail;
  grantors: DocumentDetailParty[];
  grantees: DocumentDetailParty[];
  trs: DocumentTrs[];
  batch_events: DocumentBatchEvent[];
  referenced_by_pool_orders: DocumentReferencedPoolOrder[];
}

async function jsonReq<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    let detail = r.statusText;
    try { detail = (await r.json()).error ?? detail; } catch {}
    throw new Error(`${r.status} ${detail}`);
  }
  return r.json() as Promise<T>;
}

export interface NewAddressInput {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postal?: string | null;
  country?: string | null;
}

export interface PartySearchResult {
  party_id: string;
  score: number;
  /** Which match tier hit first — useful for the palette to label
   *  why a result came back ('exact' / 'prefix' / 'trigram' /
   *  'alias-prefix' / 'alias-trigram' / 'phone'). */
  via: string;
  display_name: string;
  display_name_natural: string | null;
  normalized_name: string;
  entity_type: string;
  skip_trace_city: string | null;
  skip_trace_state: string | null;
  last_pushed_to_close_at: string | null;
  skipped_at: string | null;
  skip_traced_at: string | null;
  deceased: boolean;
  needs_review: boolean;
  has_phone: boolean;
  has_address: boolean;
  lead_list_status: 'pending' | 'researched' | null;
  on_lead_list: boolean;
}

export interface PartySearchResponse {
  query: string;
  normalized?: string;
  phone_needle?: string | null;
  results: PartySearchResult[];
  hit_cap: boolean;
}

export const partyApi = {
  get: (id: string) => jsonReq<PartyDetailResponse>(`/api/mi/parties/${id}`),

  /** Fuzzy party search. q is the operator's raw input — server
   *  normalizes via the same normalizeParty path ingest uses. */
  search: (
    q: string,
    opts: { limit?: number; onLeadList?: boolean; entityTypes?: string[] } = {},
  ) => {
    const params = new URLSearchParams({ q });
    if (opts.limit !== undefined)               params.set('limit', String(opts.limit));
    if (opts.onLeadList)                        params.set('on_lead_list', 'true');
    if (opts.entityTypes && opts.entityTypes.length > 0) {
      params.set('entity_type', opts.entityTypes.join(','));
    }
    return jsonReq<PartySearchResponse>(`/api/mi/parties/search?${params.toString()}`);
  },
  addPhone: (partyId: string, phone: string) =>
    jsonReq<{ party_id: string; phone_e164: string; source: string; is_current: boolean }>(
      `/api/mi/parties/${partyId}/phones`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone }) }
    ),
  deletePhone: (partyId: string, phoneId: string) =>
    jsonReq<{ party_id: string; deleted_phone_id: string; was_current: boolean }>(
      `/api/mi/parties/${partyId}/phones/${phoneId}`,
      { method: 'DELETE' }
    ),
  addAddress: (partyId: string, body: NewAddressInput) =>
    jsonReq<{ party_id: string; address_id: string; is_current: boolean }>(
      `/api/mi/parties/${partyId}/addresses`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),
  deleteAddress: (partyId: string, addressId: string) =>
    jsonReq<{ party_id: string; deleted_address_id: string; was_current: boolean }>(
      `/api/mi/parties/${partyId}/addresses/${addressId}`,
      { method: 'DELETE' }
    ),
  patchNotes: (partyId: string, notes: string | null) =>
    jsonReq<{ party_id: string; notes: string | null; updated_at: string }>(
      `/api/mi/parties/${partyId}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes }) }
    ),
  patchSkipped: (partyId: string, skipped: boolean) =>
    jsonReq<{ party_id: string; skipped_at: string | null; updated_at: string }>(
      `/api/mi/parties/${partyId}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ skipped }) }
    ),
  /** Update skip-trace location (city and/or state). Either field can
   *  be null to clear it. The server stamps skip_trace_location_set_at
   *  and sets source='manual' whenever either field is touched. */
  patchSkipTraceLocation: (
    partyId: string,
    body: { skip_trace_city?: string | null; skip_trace_state?: string | null },
  ) =>
    jsonReq<{
      party_id: string;
      skip_trace_city: string | null;
      skip_trace_state: string | null;
      skip_trace_location_source: string | null;
      skip_trace_location_set_at: string | null;
      updated_at: string;
    }>(
      `/api/mi/parties/${partyId}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),
  /** Pick a different Enformion candidate for this party from the
   *  stored mi.parties.enformion_candidates snapshot. Re-runs the
   *  phone-merge against the chosen person's phoneNumbers, clears
   *  needs_review, preserves the candidates jsonb so the operator
   *  can re-review again later. Free — no Enformion API call. */
  skipTracePick: (partyId: string, tahoeId: string) =>
    jsonReq<{
      party_id: string;
      tahoe_id: string;
      already_current: boolean;
      inserted_phones: number;
      promoted_phone: string | null;
      deceased: boolean;
    }>(`/api/mi/parties/${partyId}/skip-trace-pick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tahoe_id: tahoeId }),
    }),

  /** Lightweight per-party docs list (any TRS), used by the inline
   *  expand on lead-list rows. Mirrors PartyDocument shape minus the
   *  role/enrichment_status fields the AOI surface doesn't need. */
  docs: (partyId: string) =>
    jsonReq<{ documents: Array<{
      doc_number: string;
      ordinal: number;
      doc_type: string | null;
      recording_date: string | null;
      effective_date: string | null;
      primary_term_months: number | null;
      royalty_pct: string | null;
      gross_acreage: string | null;
      lessee: string | null;
      trs_label: string | null;
    }> }>(`/api/mi/parties/${partyId}/docs`),
  markResearched: (partyId: string) =>
    jsonReq<{ party_id: string; updated: number }>(
      `/api/mi/parties/${partyId}/mark-researched`,
      { method: 'POST' }
    ),
};

export const documentApi = {
  get: (docNumber: string) =>
    jsonReq<DocumentDetailResponse>(`/api/mi/documents/${encodeURIComponent(docNumber)}`),
};
